// StillReadingPage — the Quiet Check-in's answer page (win-back part A, lane 22c).
//
//   /still-reading/:token?a=keep    "Yes, keep it coming" in a check-in email
//   /still-reading/:token?a=pause   "Take a break for three months"
//
// The signed token binds one check-in series and the reader's email; the
// worker (reengagement-routes.ts → reengagement.ts) refuses anything else.
// The email link only opens this page, so a mail scanner that fetches it
// records nothing. "Keep" is recorded on load, as the issue rating is
// (decision mcd_d9964b3f-1bb), unless the browser reports itself automated;
// a break always takes a tap, because it stops the letter for three months.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

type State = 'active' | 'kept' | 'paused' | 'engaged' | 'left' | 'sunset' | 'cancelled';

interface PageData {
  firstName: string | null;
  state: State;
  pausedUntil: string | null;
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.12, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 22px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: '2px solid #F05A28', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginRight: 10, marginBottom: 10 };
const btnOutline: CSSProperties = { ...btn, background: '#fff', color: '#F05A28' };
const small: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5 };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

function longDate(iso: string | null): string {
  if (!iso) return 'in three months';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'in three months';
  return `on ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

export function StillReadingPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const asked = params.get('a') === 'pause' ? 'pause' : 'keep';
  const [data, setData] = useState<PageData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'missing' | 'error'>('loading');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const autoSent = useRef(false);

  const send = async (action: 'keep' | 'pause') => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/public/still-reading/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}).`); return; }
      setData(d.page as PageData);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    fetch(`${API}/api/public/still-reading/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 401) { setState('invalid'); return; }
        if (r.status === 404) { setState('missing'); return; }
        if (!r.ok) { setState('error'); return; }
        const page = (await r.json()) as PageData;
        setData(page);
        setState('ready');
        // "Keep" records on load for a person; an automated browser gets the button.
        const automated = typeof navigator !== 'undefined' && (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
        if (asked === 'keep' && !automated && page.state !== 'kept' && !autoSent.current) {
          autoSent.current = true;
          void send('keep');
        }
      })
      .catch(() => setState('error'));
  }, [token]);

  if (state === 'loading') return <Shell><p style={lead}>Loading…</p></Shell>;
  if (state === 'invalid') {
    return (
      <Shell>
        <h1 style={h1}>This link has expired</h1>
        <p style={lead}>No problem. Just reply to any of our emails and tell us what you would like, and we will sort it out.</p>
      </Shell>
    );
  }
  if (state !== 'ready' || !data) {
    return (
      <Shell>
        <h1 style={h1}>{state === 'missing' ? 'We could not find this link' : 'Something went wrong'}</h1>
        <p style={lead}>Please try the link again in a moment, or reply to the email and we will help.</p>
      </Shell>
    );
  }

  const hello = data.firstName ? `Thanks, ${data.firstName}.` : 'Thank you.';

  if (data.state === 'kept') {
    return (
      <Shell>
        <h1 style={h1}>You're staying on The Weekly Close</h1>
        <p style={lead}>{hello} Next Thursday's issue will be in your inbox as usual.</p>
        <p style={small}>Want a quieter few months instead?{' '}
          <button type="button" style={{ ...btnOutline, padding: '6px 12px', fontSize: 14 }} disabled={busy} onClick={() => send('pause')}>Take a break for three months</button>
        </p>
        {err && <p style={{ ...small, color: '#b42318' }}>{err}</p>}
      </Shell>
    );
  }
  if (data.state === 'paused') {
    return (
      <Shell>
        <h1 style={h1}>Enjoy the break</h1>
        <p style={lead}>{hello} The Weekly Close will pause and pick up again {longDate(data.pausedUntil)}. Nothing else changes: events, programs and your account email carry on.</p>
        <p style={small}>Changed your mind?{' '}
          <button type="button" style={{ ...btnOutline, padding: '6px 12px', fontSize: 14 }} disabled={busy} onClick={() => send('keep')}>Keep it coming instead</button>
        </p>
        {err && <p style={{ ...small, color: '#b42318' }}>{err}</p>}
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={h1}>Still want The Weekly Close?</h1>
      <p style={lead}>{asked === 'pause'
        ? 'Take a three-month break from the newsletter. Events, programs and your account email are not affected.'
        : 'One tap and it keeps coming every Thursday.'}</p>
      <div style={card}>
        {asked === 'pause' ? (
          <>
            <button type="button" style={btn} disabled={busy} onClick={() => send('pause')}>{busy ? 'Saving…' : 'Take a break for three months'}</button>
            <button type="button" style={btnOutline} disabled={busy} onClick={() => send('keep')}>Keep it coming</button>
          </>
        ) : (
          <button type="button" style={btn} disabled={busy} onClick={() => send('keep')}>{busy ? 'Saving…' : 'Yes, keep it coming'}</button>
        )}
        {err && <p style={{ ...small, color: '#b42318', marginTop: 12 }}>{err}</p>}
      </div>
    </Shell>
  );
}
